import { useGlobalModal } from '@/hooks/useGlobalModal';
import useModalStatusMessage from '@/hooks/useModalStatusMessage';
import { useLazyQuery, useQuery } from '@apollo/client';
import GlobalModal from '@components/GlobalModal';
import ModalContent from '@components/modal/ModalContent';
import ModalTitle from '@components/modal/ModalTitle';
import { msg, Trans } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { type ConfigurationFileHistoryEntry } from '@/__generated__/graphql';
import { GET_DEVICE_CONFIGURATION_FILE_HISTORY } from '../graphql/queries/getDeviceConfigurationFileHistory';
import { GET_DEVICE_CONFIGURATION_FILE_HISTORY_DOWNLOAD_URL } from '../graphql/queries/getDeviceConfigurationFileHistoryDownloadUrl';

interface ComponentProps {
  deviceId: string;
  onClose: () => void;
}

const PAGE_LIMIT = 100;

const Component: FC<ComponentProps> = ({ deviceId, onClose }) => {
  const { _ } = useLingui();
  const { openModalErrorMessage } = useModalStatusMessage();
  const [downloadingFileName, setDownloadingFileName] = useState<string | null>(null);
  const [accumulatedFiles, setAccumulatedFiles] = useState<ConfigurationFileHistoryEntry[]>([]);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const { loading, error, fetchMore } = useQuery(GET_DEVICE_CONFIGURATION_FILE_HISTORY, {
    variables: { deviceId, pagination: { limit: PAGE_LIMIT } },
    fetchPolicy: 'network-only',
    onCompleted: (result) => {
      const connection = result?.device?.hardwareConfiguration?.configurationFileHistory;
      setAccumulatedFiles(connection?.items ?? []);
      setLastEvaluatedKey(connection?.pageInfo?.lastEvaluatedKey ?? null);
    },
  });

  const [fetchDownloadUrl] = useLazyQuery(GET_DEVICE_CONFIGURATION_FILE_HISTORY_DOWNLOAD_URL);

  const files = accumulatedFiles;

  const handleLoadMore = async () => {
    if (!lastEvaluatedKey || loadingMore) return;
    setLoadingMore(true);
    try {
      const result = await fetchMore({
        variables: {
          deviceId,
          pagination: { limit: PAGE_LIMIT, exclusiveStartKey: lastEvaluatedKey },
        },
      });
      const connection = result.data?.device?.hardwareConfiguration?.configurationFileHistory;
      setAccumulatedFiles((prev) => [...prev, ...(connection?.items ?? [])]);
      setLastEvaluatedKey(connection?.pageInfo?.lastEvaluatedKey ?? null);
    } catch {
      openModalErrorMessage(_(msg`No fue posible cargar más archivos de configuración`));
    } finally {
      setLoadingMore(false);
    }
  };

  const handleDownload = async (fileName: string) => {
    setDownloadingFileName(fileName);
    try {
      const result = await fetchDownloadUrl({ variables: { deviceId, fileName } });
      const url = result.data?.deviceConfigurationFileHistoryDownloadUrl;
      if (url) {
        window.open(url, '_blank', 'noopener,noreferrer');
      } else {
        openModalErrorMessage(_(msg`No fue posible descargar el archivo de configuración`));
      }
    } catch {
      openModalErrorMessage(_(msg`No fue posible descargar el archivo de configuración`));
    } finally {
      setDownloadingFileName(null);
    }
  };

  return (
    <>
      <ModalTitle onClose={onClose} title={_(msg`Historial de archivos de configuración`)} />
      <ModalContent dividers>
        {loading && (
          <>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </>
        )}

        {!loading && error && (
          <Alert severity="error">
            <Trans>No fue posible cargar el historial de archivos de configuración</Trans>
          </Alert>
        )}

        {!loading && !error && files.length === 0 && lastEvaluatedKey == null && (
          <Typography color="textSecondary">
            <Trans>No hay archivos de configuración históricos para este equipo</Trans>
          </Typography>
        )}

        {!loading && !error && files.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Trans>Nombre del archivo</Trans>
                  </TableCell>
                  <TableCell>
                    <Trans>Fecha de creación</Trans>
                  </TableCell>
                  <TableCell align="right">
                    <Trans>Descargar</Trans>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((file) => (
                  <TableRow key={file.fileName}>
                    <TableCell sx={{ wordBreak: 'break-word' }}>{file.fileName}</TableCell>
                    <TableCell>
                      {file.createdAt ? dayjs.unix(file.createdAt).format('DD/MM/YYYY HH:mm') : '-'}
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title={_(msg`Descargar archivo`)}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file.fileName)}
                            disabled={downloadingFileName === file.fileName}
                          >
                            <DownloadIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && !error && lastEvaluatedKey != null && (
          <Button
            sx={{ mt: 1 }}
            size="small"
            disabled={loadingMore}
            onClick={handleLoadMore}
            fullWidth
            startIcon={loadingMore ? <CircularProgress size={16} /> : undefined}
          >
            <Trans>Cargar más</Trans>
          </Button>
        )}
      </ModalContent>
    </>
  );
};

// Exportado únicamente para poder testear el contenido sin pelear con el modal global
// (useGlobalModal) — ver ConfigurationFileHistoryModal.test.tsx.
export const ConfigurationFileHistoryModalContent = Component;

export const ConfigurationFileHistoryModal: FC = () => {
  const { globalModalProps, state, onClose } = useGlobalModal('configurationFileHistoryModal');

  return (
    <GlobalModal {...globalModalProps} dialogProps={{ maxWidth: 'sm', fullWidth: true }}>
      {state.open && <Component deviceId={state.deviceId} onClose={onClose} />}
    </GlobalModal>
  );
};

export default ConfigurationFileHistoryModal;
